// traceTurnFocus.js — the turn coordinate shared by the trace-detail readings.
//
// Context, X-ray, Lanes and Tree are four projections of the same
// `trace.turns` array, so a turn index is meaningful in all of them (Raw is
// the exception: it renders the verbatim payload and has no turn structure).
// This module owns the wire form of that coordinate and the number the
// readings print for it, so both can be tested without a DOM.
//
// It is ONE turn, not a range, and it is set by an explicit act — expanding a
// turn card, picking a lane cell, opening a turn in the dock. An earlier
// version derived it from scroll position as a visible range; that was dropped
// because the readings disagree about what a range even means (Lanes selects a
// single turn by construction: its dock opens on one turn and its selection is
// one cell), and because a scrolled-past turn is not a turn the reader chose.
// A coordinate the reader set deliberately is one they can predict.
//
// On the wire the coordinate is the turn's MESSAGE ID when it has one. A turn
// index is a position in whatever page of the record is loaded, and the axis
// reads a live session's record newest first, so an index named a different
// message every time the session grew. The message id names the same turn
// however the record is paged; the index remains for turns without a message
// and for links written before this.

const TURN_RE = /^\d{1,7}$/;
const MESSAGE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const TURN_FOCUS_PARAM = "turn";

/**
 * Wire form: the turn's message id when it has one, else the bare turn index;
 * null when there is nothing to write.
 */
export function formatTurnFocus(turn, messageId = null) {
    if (!Number.isInteger(turn) || turn < 0) return null;
    const id = String(messageId ?? "").trim().toLowerCase();
    return MESSAGE_RE.test(id) ? id : String(turn);
}

/** The message id a wire form names, or null when it names an index or nothing. */
export function parseTurnFocusMessage(raw) {
    const text = String(raw ?? "").trim().toLowerCase();
    return MESSAGE_RE.test(text) ? text : null;
}

/**
 * Parse the wire form as a turn index. A message id yields null here too: it
 * is a turn only once the loaded turns carry that message, which the reading
 * resolves with parseTurnFocusMessage. Anything unparseable yields null rather
 * than a guess: a hand-edited or stale URL should be ignored, not half-applied.
 */
export function parseTurnFocus(raw) {
    const text = String(raw ?? "").trim();
    if (!TURN_RE.test(text)) return null;
    const turn = Number(text);
    return Number.isSafeInteger(turn) ? turn : null;
}

/**
 * Clamp onto a trace of `turnCount` turns. Returns null when the turn does not
 * exist, so a caller skips the landing entirely rather than aiming at turn 0
 * and implying that is where the reader was.
 */
export function clampTurnFocus(turn, turnCount) {
    if (!Number.isInteger(turn) || !Number.isInteger(turnCount) || turnCount <= 0) return null;
    if (turn < 0 || turn > turnCount - 1) return null;
    return turn;
}

/**
 * Does an op row spanning `[start, end]` contain the focused turn? X-ray rows
 * cover a span of turns, so "the row for this turn" is a containment test, not
 * an equality one.
 */
export function turnFocusInSpan(turn, start, end) {
    if (!Number.isInteger(turn) || !Number.isInteger(start) || !Number.isInteger(end)) return false;
    return turn >= start && turn <= end;
}

/**
 * Pages a link's landing may read on its own before it gives up. A landing
 * names a message (?turn=) or a finding (?finding=) that can sit far behind
 * the newest page; past this the reader still has the "earlier" control.
 */
export const LANDING_PAGE_LIMIT = 40;

/**
 * May a landing read the page before the loaded one? Only on a paged session
 * record that has more to read and is not mid-read. A failed read stops it:
 * retrying is the reader's call, not a landing's.
 */
export function landingMayReadEarlier(record, pagesRead) {
    if (!record?.session || record.pending || record.loading || record.exhausted || record.rejected) return false;
    return Number.isInteger(pagesRead) && pagesRead < LANDING_PAGE_LIMIT;
}

/**
 * What every printed turn number adds to a turn's page-relative index: the
 * messages before the loaded page, so a number names the turn's place in the
 * whole record however it is paged. Zero while the record's total is not known
 * yet, when the whole record is loaded, and for a trace that is not a paged
 * session record. Messages and turns differ where a message unwraps into
 * several turns, so the base is exact for the loaded page's order and
 * approximate as an absolute count.
 */
export function recordTurnBase(record) {
    if (!record?.session) return 0;
    const shown = Number(record.loaded) || 0;
    const total = record.total != null && Number.isFinite(Number(record.total)) ? Number(record.total) : null;
    return total != null && total > shown ? total - shown : 0;
}
